import { Box, useTheme } from "@mui/material"
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import type { AnySceneObjectT } from "tweenly"
import { measureParentSpaces, SpaceT } from "../../../helpers/domSpace.helpers"
import { getObjectPositioning } from "../../../helpers/object.helpers"
import { selectUndoable } from "../../../helpers/selector.helpers"
import {
    createResizePreview,
    multiResizePreviewStyles,
    ResizePreviewT,
} from "../../../helpers/resizePreview.helpers"
import { mapBoxThroughResize } from "../../../helpers/multiResize.helpers"
import {
    Chrome,
    MemberOutline,
    Orientation,
    Rect,
    measureMultiChrome,
    measureOrientedChrome,
    measureRect,
} from "../../../helpers/selectionChrome.helpers"
import {
    applyTransform,
    orientedPoint,
    Point,
    snapDistanceAlongAxis,
    toCssMatrix,
    toLocalDelta,
} from "../../../helpers/orientedResize.helpers"
import { NOT_SNAPPED } from "../../../helpers/snap.helpers"
import { createResizeSnapSession, ResizeSnapSession } from "../../../helpers/snapSession.helpers"
import {
    computeWrapperBounds,
    domObjectSize,
    ObjectsById,
    WrapperBounds,
} from "../../../helpers/wrapper.helpers"
import useDynamicRefs from "../../../hooks/useDynamicRefs"
import { useObjectActions } from "../../../hooks/useObjectActions"
import { useAppDispatch, useAppSelector, useAppStore } from "../../../hooks/useRedux"
import { resizeSelectedObjectsAction } from "../../../store/slices/objects.slice/object/selectionActions"
import DraggableItem, {
    BOTTOM_DIRECTIONS,
    Direction,
    DRAGGABLE_ITEMS,
    LEFT_DIRECTIONS,
    PositionBox,
    ResizeDelta,
    RIGHT_DIRECTIONS,
    TOP_DIRECTIONS,
} from "./DraggableItem"
import LineHandles from "./LineHandles"

/** Dynamic-ref key the canvas element is published under, see Canvas.tsx. */
const CANVAS_REF_KEY = "canvas"

/**
 * Selection chrome — the outline, and for a single selection the resize handles too.
 *
 * Rendered as a SIBLING of the root canvas items rather than inside the selected object,
 * because an object's own `zIndex: object.index` opens a stacking context — anything drawn
 * inside it is trapped below every object with a higher index, no matter what z-index it
 * asks for. That is why the resize handles could not be grabbed once another object covered
 * the selection. Up here they always sit on top.
 *
 * The box itself is `pointer-events: none`, so the browser's own hit test keeps deciding
 * what a click selects (the topmost object, as before). Only the handles opt back in.
 */

/** Dynamic-ref key, so the drag gesture can move the chrome along with the object. */
export const SELECTION_OVERLAY_REF_KEY = "selection-overlay"

/** Above every object AND above the multiselection wrapper (2500), which it also frames. */
const SELECTION_OVERLAY_Z_INDEX = 2600

type Props = {
    canvasScaleCoef: number
    zoomScale: number
}

/**
 * A resize in progress. Everything is captured when the gesture starts, so the preview keeps
 * working from the geometry the handle was grabbed at — the DOM it measures is being rewritten
 * by the preview itself.
 */
type ResizeSession = {
    chrome: Chrome
    preview: ResizePreviewT
    /**
     * multiselection only: the members as they were, the store they came from, the box the
     * selection was DRAWN in (`before`, what the handles drag) and the box its styles span
     * (`styleBefore`, what the maths scales — see `mapBoxThroughResize`)
     */
    multi?: {
        bounds: WrapperBounds
        before: Rect
        styleBefore: Rect
        objectsById: ObjectsById
        /** see DrawnResizeT — measured before the preview starts rewriting the DOM */
        parentSpaces: Record<string, SpaceT>
    }
    /** snap to objects — null when it is switched off */
    snap: ResizeSnapSession | null
    /**
     * A turned object: where the dragged handle's point ended up on the canvas after the last
     * snap — exactly on the guide when it snapped. The guides are drawn from it rather than
     * from the box measured off the screen, which misses the guide by a fraction of a px.
     */
    turnedHandlePoint?: Point
}

/**
 * Where a handle sits on its box, as fractions of the width and height (0 = left / top,
 * 1 = right / bottom) — the point of a turned object that snaps.
 */
const handleFractions = (direction: Direction) => ({
    fx: LEFT_DIRECTIONS.includes(direction) ? 0 : RIGHT_DIRECTIONS.includes(direction) ? 1 : 0.5,
    fy: TOP_DIRECTIONS.includes(direction) ? 0 : BOTTOM_DIRECTIONS.includes(direction) ? 1 : 0.5,
})

/**
 * Where a member outline lands when the selection box is scaled from `before` to `after` —
 * the drawing counterpart of `scaleObjectsInBounds`, mirrored the same way: its centre keeps
 * its relative place. A turned outline is stretched exactly like the turned object is (see
 * affineMemberScale), by drawing it through the selection's scale on top of its own turn.
 */
const scaleMember = (member: MemberOutline, before: Rect, after: PositionBox): MemberOutline => {
    const scaleX = before.width > 0 ? Math.max(after.width, 1) / before.width : 1
    const scaleY = before.height > 0 ? Math.max(after.height, 1) / before.height : 1
    const { transform } = member
    const width = transform ? member.width : member.width * scaleX
    const height = transform ? member.height : member.height * scaleY
    const centreX = (member.left + member.width / 2) * scaleX
    const centreY = (member.top + member.height / 2) * scaleY
    return {
        ...member,
        width,
        height,
        left: (after.isMirroredX ? after.width - centreX : centreX) - width / 2,
        top: (after.isMirroredY ? after.height - centreY : centreY) - height / 2,
        transform: transform && {
            a: transform.a * scaleX,
            b: transform.b * scaleY,
            c: transform.c * scaleX,
            d: transform.d * scaleY,
        },
    }
}

const sameMember = (a: MemberOutline, b: MemberOutline): boolean =>
    sameRect(a, b) &&
    a.ellipse === b.ellipse &&
    (a.transform === b.transform ||
        (a.transform !== undefined &&
            b.transform !== undefined &&
            (["a", "b", "c", "d"] as const).every(
                (key) => a.transform![key] === b.transform![key]
            )))

const sameRect = (a: Rect, b: Rect): boolean =>
    a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height

const sameOrientation = (a?: Orientation, b?: Orientation): boolean =>
    a === b ||
    (a !== undefined &&
        b !== undefined &&
        (["a", "b", "c", "d"] as const).every(
            (key) => a.total[key] === b.total[key] && a.own[key] === b.own[key]
        ))

/**
 * A single object's chrome: turned with the object when it is turned (so the handles sit on
 * its sides), otherwise its box as measured off the screen. A line is never turned here —
 * LineHandles draws its ends from its own geometry.
 */
const measureSingleChrome = (
    object: AnySceneObjectT,
    element: HTMLElement,
    canvasElement: HTMLElement,
    scale: number
): Chrome =>
    (object.type !== "line" && measureOrientedChrome(element, canvasElement, scale)) || {
        box: measureRect(element, canvasElement, scale),
        members: [],
    }

const sameChrome = (a: Chrome | null, b: Chrome): boolean =>
    a !== null &&
    sameOrientation(a.orientation, b.orientation) &&
    sameRect(a.box, b.box) &&
    a.members.length === b.members.length &&
    a.members.every((member, index) => sameMember(member, b.members[index]))

const SelectionOverlay = ({ canvasScaleCoef, zoomScale }: Props) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const store = useAppStore()
    const { updateObjectStyles } = useObjectActions()
    const [getRef, setRef] = useDynamicRefs<HTMLElement>()

    const selectedIds = useAppSelector((state) => state.activeObject.selected)
    // The whole map on purpose: the chrome is placed in ABSOLUTE canvas coordinates, so it
    // also has to follow when an ANCESTOR of the selected object moves — subscribing to the
    // selected object alone would leave it behind. One component re-rendering per object
    // change is a price worth paying for that.
    const objectsById = useAppSelector((state) => selectUndoable(state).objects.value)
    const isEditingTextMode = useAppSelector((state) => state.editor.value.isEditingTextMode)

    const isMultiSelection = selectedIds.length > 1
    const object = selectedIds.length === 1 ? objectsById[selectedIds[0]] : undefined

    const scale = canvasScaleCoef * zoomScale || 1
    const [chrome, setChrome] = useState<Chrome | null>(null)

    useLayoutEffect(() => {
        let measured: Chrome | null = null

        const canvasElement = getRef(CANVAS_REF_KEY)?.current
        /**
         * Measured off the objects themselves, like a single object — the styles say nothing
         * about rotation or any other transform, so a turned object stuck out of a box built
         * from them. (Not off `#objects-wrapper`: that element unmounts for a frame at the end
         * of a drag, the objects never do.)
         */
        const measuredMulti =
            isMultiSelection && canvasElement
                ? measureMultiChrome(
                      selectedIds,
                      objectsById,
                      canvasElement,
                      scale,
                      (id) => getRef(id)?.current
                  )
                : null

        if (measuredMulti) {
            measured = measuredMulti
        } else if (isMultiSelection) {
            // not rendered yet — the styles are the best guess until it is
            const bounds = computeWrapperBounds(selectedIds, objectsById, domObjectSize)
            const box = {
                top: bounds.origin.top,
                left: bounds.origin.left,
                width: bounds.width,
                height: bounds.height,
            }
            measured = {
                box,
                /**
                 * `objectsAbsolute` also carries every DESCENDANT of a selected object — the
                 * union box has to span those too — but only the selected objects themselves
                 * get outlined, so the rest is filtered out here.
                 */
                members: bounds.objectsAbsolute
                    .filter((entry) => selectedIds.indexOf(entry.objectId) !== -1)
                    .map((entry) => ({
                        top: entry.top - box.top,
                        left: entry.left - box.left,
                        width: entry.width,
                        height: entry.height,
                    })),
            }
        } else if (object) {
            /**
             * A single object is measured from the DOM instead.
             *
             * Its styles only tell the truth for absolutely positioned objects with numeric
             * sizes: a flex child is placed by its parent's layout and a text object sizes
             * itself, so both would be framed in the wrong place.
             */
            const element = getRef(object.id)?.current
            if (element && canvasElement) {
                measured = measureSingleChrome(object, element, canvasElement, scale)
            }
        }

        if (!measured) {
            setChrome(null)
            return
        }

        // objectsById changes on every store write; without this the overlay would re-render
        // on each of them even when the selection has not actually moved
        const next = measured
        setChrome((previous) => (sameChrome(previous, next) ? previous : next))
    }, [object, isMultiSelection, selectedIds, objectsById, scale, getRef])

    /**
     * Resizing writes parent-relative top/left, so the maths keeps running on the object's
     * own styles — only the DRAWING uses the measured absolute rect.
     *
     * Both are memoized: this component re-renders on every write to the objects slice, and
     * the eight handles are `React.memo`, which a fresh object literal would defeat.
     */
    const objectPositioning = useMemo(
        () => (object ? getObjectPositioning(object) : null),
        [object]
    )
    /**
     * While a handle is dragged the chrome is drawn from the live box instead, so the outline
     * and all eight handles stay on the edges of the object as it is being resized.
     */
    const [liveChrome, setLiveChrome] = useState<Chrome | null>(null)
    const shownChrome = liveChrome ?? chrome

    const renderPosition = useMemo(
        () => ({
            position: "absolute" as const,
            top: 0,
            left: 0,
            width: shownChrome?.box.width ?? 0,
            height: shownChrome?.box.height ?? 0,
        }),
        [shownChrome?.box.width, shownChrome?.box.height]
    )

    /**
     * The containers used to be told whether they were locked / hidden / inside a ticker by
     * their parent, through props. Up here there is no parent to ask, so the same answer is
     * read off the object chain — and for a multiselection, off EVERY member's chain: one
     * locked object is enough to make resizing the group wrong.
     */
    const isResizable = useMemo(
        () =>
            selectedIds.length > 0 &&
            selectedIds.every((id) => {
                const selected = objectsById[id]
                if (!selected) return false
                // a flex child is sized by its parent's layout — a resize would be overwritten
                if (getObjectPositioning(selected).position === "static") return false

                let ancestor: AnySceneObjectT | undefined = selected
                while (ancestor) {
                    if (ancestor.isLocked || ancestor.isHidden) return false
                    if (ancestor !== selected && ancestor.type === "ticker") return false
                    ancestor = ancestor.parentId ? objectsById[ancestor.parentId] : undefined
                }
                return true
            }),
        [selectedIds, objectsById]
    )

    /**
     * One object writes its own styles; a multiselection is scaled inside its new bounding
     * box. The handle itself stays ignorant of the difference — it only reports the box.
     */
    /**
     * The space the resize maths runs in, which is NOT the same for the two cases.
     *
     * A single object commits parent-relative top/left, so the handles have to work against
     * its own styles. A multiselection has no such thing — its box lives in absolute canvas
     * coordinates, and `resizeSelectedObjectsAction` converts back to each member's own
     * frame of reference on commit.
     */
    const resizeSpace = isMultiSelection
        ? chrome
            ? { position: "absolute" as const, ...chrome.box }
            : null
        : objectPositioning

    const sessionRef = useRef<ResizeSession | null>(null)

    const endSession = () => {
        sessionRef.current?.preview.restore()
        sessionRef.current?.snap?.end()
        sessionRef.current = null
        setLiveChrome(null)
    }

    // the selection can change (or the overlay unmount) mid-gesture — never leave a preview
    // (or the snap guides) behind
    useEffect(
        () => () => {
            sessionRef.current?.preview.restore()
            sessionRef.current?.snap?.end()
        },
        []
    )

    const handleResizeStart = () => {
        if (!chrome || !resizeSpace) return
        const bounds = isMultiSelection
            ? computeWrapperBounds(selectedIds, objectsById, domObjectSize)
            : undefined
        const canvasElement = getRef(CANVAS_REF_KEY)?.current
        sessionRef.current = {
            chrome,
            preview: createResizePreview(),
            // measured here, before the preview starts rewriting the selection's DOM
            snap: store.getState().editor.value.settings.canvasSnapToObjectsOn
                ? createResizeSnapSession(selectedIds, objectsById)
                : null,
            multi: bounds
                ? {
                      bounds,
                      before: chrome.box,
                      styleBefore: {
                          top: bounds.origin.top,
                          left: bounds.origin.left,
                          width: bounds.width,
                          height: bounds.height,
                      },
                      objectsById,
                      parentSpaces: canvasElement
                          ? measureParentSpaces(
                                bounds.objectsAbsolute.map((entry) => entry.objectId),
                                (id) => objectsById[id]?.parentId,
                                (id) => getRef(id)?.current,
                                canvasElement
                            )
                          : {},
                  }
                : undefined,
        }
    }

    /**
     * Snap the edges the handle drags onto other objects.
     *
     * The edges are taken from the chrome, which is in absolute canvas units like the snap
     * targets. The delta itself is the same in the handle's own space, so it can go straight
     * back to the handle.
     */
    const handleSnapDelta = (direction: Direction, delta: ResizeDelta, bypass: boolean) => {
        const session = sessionRef.current
        if (!session?.snap) return { delta, snapped: NOT_SNAPPED }
        const { box, orientation: turned } = session.chrome
        if (turned) return snapTurnedHandle(session.snap, direction, delta, bypass)
        return session.snap.snap(
            {
                x: LEFT_DIRECTIONS.includes(direction)
                    ? box.left
                    : RIGHT_DIRECTIONS.includes(direction)
                    ? box.left + box.width
                    : undefined,
                y: TOP_DIRECTIONS.includes(direction)
                    ? box.top
                    : BOTTOM_DIRECTIONS.includes(direction)
                    ? box.top + box.height
                    : undefined,
            },
            delta,
            bypass
        )
    }

    /**
     * Snap a handle of a TURNED object: the handle's own point is what snaps, the way a line's
     * end does — its sides run at an angle, so they cannot sit on the canvas-aligned guides.
     *
     * `delta` is along the object's own sides (see DraggableItem). A corner moves freely, so
     * its canvas x and y snap on their own. An edge only travels along its side, so it stops
     * where that path meets the nearest guide.
     */
    const snapTurnedHandle = (
        snap: ResizeSnapSession,
        direction: Direction,
        delta: ResizeDelta,
        bypass: boolean
    ) => {
        const session = sessionRef.current!
        const { box, orientation } = session.chrome
        const m = orientation!.total
        const { fx, fy } = handleFractions(direction)
        const start = orientedPoint(box, m, fx, fy)
        const pointerMove = applyTransform(m, delta)
        const result = snap.snap({ x: start.x, y: start.y }, pointerMove, bypass)
        const landAt = (move: Point) => {
            session.turnedHandlePoint = { x: start.x + move.x, y: start.y + move.y }
        }
        landAt(pointerMove)
        if (!result.snapped.x && !result.snapped.y) return { delta, snapped: NOT_SNAPPED }

        const isEdgeX = fy === 0.5 // left / right: travels along the object's own x
        const isEdgeY = fx === 0.5
        if (!isEdgeX && !isEdgeY) {
            landAt(result.delta)
            return { delta: toLocalDelta(m, result.delta), snapped: { x: true, y: true } }
        }
        const axis = applyTransform(m, isEdgeX ? { x: 1, y: 0 } : { x: 0, y: 1 })
        const distance = snapDistanceAlongAxis(axis, result, isEdgeX ? delta.x : delta.y)
        if (distance === null) return { delta, snapped: NOT_SNAPPED }
        landAt({ x: axis.x * distance, y: axis.y * distance })
        return isEdgeX
            ? { delta: { x: distance, y: 0 }, snapped: { x: true, y: false } }
            : { delta: { x: 0, y: distance }, snapped: { x: false, y: true } }
    }

    /**
     * Preview the box on the objects themselves and redraw the chrome around it.
     *
     * A single object gets the box as its own parent-relative geometry. A multiselection
     * gets exactly what `resizeSelectedObjectsAction` will commit on release.
     */
    const handleResizing = (box: PositionBox) => {
        const session = sessionRef.current
        if (!session) return

        if (session.multi) {
            const { bounds, before, styleBefore, objectsById: startObjects } = session.multi
            const styleAfter = mapBoxThroughResize(styleBefore, before, box)
            multiResizePreviewStyles(
                bounds.objectsAbsolute,
                styleBefore,
                styleAfter,
                startObjects,
                {
                    before,
                    after: box,
                    parentSpaces: session.multi.parentSpaces,
                }
            ).forEach(({ objectId, styles }) => {
                const element = getRef(objectId)?.current
                if (element) session.preview.apply(element, styles)
            })
            const liveBox = { top: box.top, left: box.left, width: box.width, height: box.height }
            session.snap?.showGuides(liveBox)
            setLiveChrome({
                box: liveBox,
                members: session.chrome.members.map((member) => scaleMember(member, before, box)),
            })
        } else if (object) {
            const element = getRef(object.id)?.current
            const canvasElement = getRef(CANVAS_REF_KEY)?.current
            if (!element || !canvasElement) return
            session.preview.apply(element, {
                left: box.left,
                top: box.top,
                width: box.width,
                height: box.height,
            })
            /**
             * The chrome is measured off the previewed element, exactly like the resting chrome.
             *
             * It used to be derived from the box by the measured/styled ratio at the start of
             * the gesture, but a border or padding (content-box) adds a CONSTANT to the rendered
             * size, not a factor — so the outline drifted further from the object the further
             * the handle was dragged.
             */
            const live = measureSingleChrome(object, element, canvasElement, scale)
            const point = session.turnedHandlePoint
            if (live.orientation && point) {
                // a turned object snaps by the dragged handle's point, see snapTurnedHandle
                session.snap?.showGuides({ left: point.x, top: point.y, width: 0, height: 0 })
            } else {
                session.snap?.showGuides(live.box)
            }
            setLiveChrome(live)
        }
    }

    const handleResizeCancel = () => endSession()

    const handleResize = (box: PositionBox) => {
        // back to the committed geometry first: the multiselection commit measures the DOM,
        // and React only writes the styles that differ from what it rendered last time. Both
        // happen in this same task, so nothing is painted in between.
        const drawnBefore = sessionRef.current?.multi?.before
        endSession()

        if (isMultiSelection) {
            dispatch(resizeSelectedObjectsAction(box, drawnBefore))
        } else if (object) {
            const { width, height, left, top } = box
            updateObjectStyles(object, { width, height, left, top })
        }
    }

    if (!chrome) return null

    const { box, members } = shownChrome ?? chrome
    // from the resting chrome: the same object while a handle is dragged, and a stable
    // reference for the memoised handles
    const orientation = chrome.orientation
    const transformOrigin = object?.styles.find((style) => style.property === "transformOrigin")
        ?.value as string | undefined
    const outline = `${1 / scale}px solid ${theme.palette.primary.light}`
    const showHandles = isResizable && !isEditingTextMode
    // a single line is selected by its two ends, not a box (see LineHandles)
    const line = object?.type === "line" ? object : undefined
    // a single ellipse is outlined as well as framed, the way a multiselection outlines it
    const isEllipse = object?.type === "ellipse"

    return (
        <Box
            ref={setRef(SELECTION_OVERLAY_REF_KEY)}
            style={{
                position: "absolute",
                top: box.top,
                left: box.left,
                width: box.width,
                height: box.height,
                zIndex: SELECTION_OVERLAY_Z_INDEX,
                pointerEvents: "none",
                // outline, not border: drawn outside the box, so it cannot shift the handles
                outline: line ? "none" : outline,
                // a turned object: the outline and the handles turn with it, about the
                // box's centre — which is where the object is drawn (measureOrientedChrome)
                transform: orientation ? toCssMatrix(orientation.total) : undefined,
                transformOrigin: "center",
            }}
        >
            {isEllipse && (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "50%",
                        outline,
                    }}
                />
            )}
            {/**
             * One outline per selected object, drawn before the handles so those stay on
             * top. No handles of their own — the group's corners resize the whole selection.
             */}
            {members.map((member, index) => (
                <div
                    key={index}
                    style={{
                        position: "absolute",
                        top: member.top,
                        left: member.left,
                        width: member.width,
                        height: member.height,
                        outline,
                        // drawn as Figma draws it: turned with the object, an ellipse as one
                        transform: member.transform ? toCssMatrix(member.transform) : undefined,
                        transformOrigin: "center",
                        borderRadius: member.ellipse ? "50%" : undefined,
                    }}
                />
            ))}

            {line && (
                <LineHandles
                    object={line}
                    objectsById={objectsById}
                    scale={scale}
                    origin={box}
                    interactive={showHandles}
                />
            )}

            {!line &&
                resizeSpace &&
                showHandles &&
                DRAGGABLE_ITEMS.map((item) => (
                    <DraggableItem
                        key={item.direction}
                        position={resizeSpace}
                        orientation={orientation}
                        transformOrigin={transformOrigin}
                        renderPosition={renderPosition}
                        onResize={handleResize}
                        onResizeStart={handleResizeStart}
                        onResizing={handleResizing}
                        onResizeCancel={handleResizeCancel}
                        snapDelta={handleSnapDelta}
                        canvasScaleCoef={canvasScaleCoef}
                        direction={item.direction}
                        axis={item.axis}
                        disabled={false}
                    />
                ))}
        </Box>
    )
}

export default SelectionOverlay
